import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  getPaginationItems,
  getTotalPages,
  type PaginationItem,
} from '../../../utils/pagination.util';

@Component({
  selector: 'app-table-pagination',
  templateUrl: './table-pagination.component.html',
})
export class TablePaginationComponent {
  @Input() currentPage = 1;
  @Input() totalItems = 0;
  @Input() itemsPerPage = 100;
  /** Tailwind třídy obalu (např. `mt-6` nebo `mb-3`). */
  @Input() wrapperClass = '';

  /** `bar` — rozsah vlevo, ovládání vpravo (lišta tabulky). `center` — původní vystředěný pager. */
  @Input() layout: 'bar' | 'center' = 'center';

  /** Pod stránkováním: „Zobrazit všechny řádky“ / návrat ke stránkování. */
  @Input() showAllAction = false;
  @Input() showAllActive = false;
  @Input() showAllLoading = false;

  @Output() pageChange = new EventEmitter<number>();
  @Output() showAllClick = new EventEmitter<void>();
  @Output() exitShowAllClick = new EventEmitter<void>();

  get visible(): boolean {
    return !this.showAllActive && this.totalItems > this.itemsPerPage;
  }

  /** Range line stays under the toolbar even when everything fits on one page. */
  get showRange(): boolean {
    return this.totalItems > 0;
  }

  get rangeStart(): number {
    if (this.totalItems <= 0) return 0;
    if (this.showAllActive) return 1;
    return (Math.max(1, this.currentPage) - 1) * Math.max(1, this.itemsPerPage) + 1;
  }

  get rangeEnd(): number {
    if (this.totalItems <= 0) return 0;
    if (this.showAllActive) return this.totalItems;
    return Math.min(Math.max(1, this.currentPage) * Math.max(1, this.itemsPerPage), this.totalItems);
  }

  formatCount(value: number): string {
    return new Intl.NumberFormat('cs-CZ').format(Math.max(0, value));
  }

  jumpTo(raw: string): void {
    const page = Number.parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(page)) return;
    this.goToPage(page);
  }

  get showAllControlsVisible(): boolean {
    if (!this.showAllAction) return false;
    return this.showAllActive || this.totalItems > this.itemsPerPage;
  }

  get totalPages(): number {
    return getTotalPages(this.totalItems, this.itemsPerPage);
  }

  get paginationItems(): PaginationItem[] {
    return getPaginationItems(this.currentPage, this.totalItems, this.itemsPerPage);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.pageChange.emit(page);
  }
}
